import { inject } from 'vue'
import { joinURL } from 'ufo'
import { NUXT_PROXY_PREFIX } from '~/lib/constants'
import { ServerClientKey } from '~/api/symbols'
import axios, { isAxiosError } from 'axios'
import mime from 'mime'
import type { VersionResponse } from '@gridcon-energy/schaltungstool-backend'

export type ServerClient = ReturnType<typeof createServerClient>
export type ServerClientOptions = {
  apiBaseURL?: string
  publicBaseURL?: string
}

/**
 * When using "responseTypeBlob" but the error is JSON.
 */
async function blobResponseError(error: Error) {
  if (isAxiosError(error) && error.response?.data instanceof Blob) {
    const blob = error.response.data
    if (blob.type.includes('application/json') || error.response.headers['content-type'] === 'application/json') {
      const text = await blob.text()
      const json = JSON.parse(text)
      return json
    }
  }

  throw error
}

/**
 * A client to communicate with Nuxt server routes and public assets.
 */
export function createServerClient(options: ServerClientOptions = {}) {
  const { apiBaseURL = NUXT_PROXY_PREFIX, publicBaseURL = '' } = options
  const ARIAL_URL = joinURL(publicBaseURL, '/Arial.ttf')
  const FRONTEND_VERSION_URL = joinURL(publicBaseURL, '/version')
  const PDF2PNG_URL = joinURL(apiBaseURL, '/pdf2png')

  /**
   * Used by PDFWriter to embed Font
   *
   * In node its Uint8Array, in browser ArrayBuffer
   */
  async function arial(): Promise<ArrayBuffer | Uint8Array> {
    const response = await axios.get<ArrayBuffer | Uint8Array>(ARIAL_URL, { responseType: 'arraybuffer' })
    return response.data
  }

  /**
   * Uploads a PDF (File or ArrayBuffer) to /api/pdf-to-image and processes the response.
   */
  async function pdf2png(pdf: File): Promise<File> {
    const formData = new FormData()
    formData.append('file', pdf, pdf.name)
    try {
      const response = await axios.post<Blob>(PDF2PNG_URL, formData, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      const extension = mime.getExtension(response.data.type)
      const filename = pdf.name.replace('.pdf', `.${extension}`)
      return new File([response.data], filename, { type: 'image/png' })
    } catch (error) {
      throw await blobResponseError(error as Error)
    }
  }

  async function frontendVersion(): Promise<VersionResponse> {
    const response = await axios.get<VersionResponse>(FRONTEND_VERSION_URL)
    return response.data
  }

  return {
    arial: arial,
    frontendVersion: frontendVersion,
    pdf2png: pdf2png,
  }
}

export function injectServerClient() {
  const server = inject(ServerClientKey)
  if (!server) {
    throw Error('Server client missing')
  }
  return server
}
